import React, { useState } from "react";
import FormField from "./FormField";
import { useFormikContext } from "formik";
import { Button, IconButton, Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import TrashIcon from "assets/icons/Trash.svg";
import InputMask from "react-input-mask";

const Step6 = ({
  fields,
  showAddReference,
  setShowAddReference,
  newReference,
  handleAddReference,
  handleRemoveReference,
  handleCancelAdd,
  handleInputChange,
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const [referenceErrors, setReferenceErrors] = useState({});

  const references = values.references || [];

  const onAddReference = () => {
    // Clear previous errors
    setReferenceErrors({});

    // Validate the new reference
    const errors = {};
    if (!newReference.first_name.trim()) {
      errors.first_name = "First name is required";
    }
    if (!newReference.last_name.trim()) {
      errors.last_name = "Last name is required";
    }
    if (!newReference.phone.trim()) {
      errors.phone = "Phone number is required";
    } else {
      const digits = newReference.phone.replace(/\D/g, "");
      if (digits.length !== 10) {
        errors.phone = "Phone must be 10 digits";
      }
    }
    if (
      newReference.email &&
      newReference.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newReference.email)
    ) {
      errors.email = "Invalid email format";
    }

    if (Object.keys(errors).length > 0) {
      setReferenceErrors(errors);
      return;
    }

    handleAddReference(setFieldValue, values);
  };

  const onRemoveReference = (index) => {
    handleRemoveReference(index, setFieldValue, values);
  };

  const onCancelAdd = () => {
    setReferenceErrors({});
    handleCancelAdd();
  };

  const onInputChange = (field, value) => {
    // Clear error for this field when user starts typing
    if (referenceErrors[field]) {
      const newErrors = { ...referenceErrors };
      delete newErrors[field];
      setReferenceErrors(newErrors);
    }
    handleInputChange(field, value);
  };

  return (
    <div className="w-full space-y-6">
      {/* References Section */}
      <div className="space-y-2">
        {/* Existing References */}
        {references.map((reference, index) => (
          <Box
            key={index}
            sx={{
              borderRadius: "16px",
              paddingY: { md: 2, xs: 2 },
              paddingX: { md: 3, xs: 2 },
              gap: 4,
              display: "flex",

              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #ECECEC",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: "#242424",
              }}
              variant="h2"
            >
              {reference.first_name} {reference.last_name}
            </Typography>
            <IconButton
              sx={{
                border: "1px solid #FFE1E1",
                padding: { md: "12px", xs: "8px" },
                backgroundColor: "#FEF2F2",
                borderRadius: "12px",
                fontSize: { md: "12px", xs: "10px" },
              }}
              onClick={() => onRemoveReference(index)}
              size="small"
            >
              <img src={TrashIcon} alt="Trash" />
            </IconButton>
          </Box>
        ))}

        {/* Add Reference Form */}
        {showAddReference && (
          <Box
            sx={{
              maxWidth: "776px",
              borderRadius: "16px",
              paddingY: { md: 2, xs: 1 },
              paddingX: { md: 3, xs: 2 },
              gap: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid #ECECEC",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 2, md: 0 },
              }}
            >
              {" "}
              <Typography sx={{ color: "#242424" }} variant="h2">
                Add new reference
              </Typography>
              <Box sx={{ display: "flex", gap: "12px" }}>
                {" "}
                <Button
                  sx={{
                    color: "#242424",
                    backgroundColor: "#F8F8F8",
                    border: "1px solid #ECECEC",
                    borderRadius: "12px",
                    padding: "8px 16px",
                    letterSpacing: "0.025em",
                    height: "38px",
                    fontSize: "12px",
                  }}
                  onClick={onCancelAdd}
                >
                  Cancel
                </Button>
                <Button
                  sx={{
                    color: "#FFFFFF",
                    backgroundColor: "#3CAB5C",
                    borderRadius: "12px",
                    padding: "8px 16px",
                    letterSpacing: "0.025em",
                    border: "1px solid #3CAB5C",
                    height: "38px",
                    fontSize: "12px",
                    "&:hover": {
                      color: "#3CAB5C",
                      border: "1px solid #3CAB5C",
                      backgroundColor: "#FFFFFF",
                    },
                  }}
                  onClick={onAddReference}
                >
                  Save
                </Button>
              </Box>
            </Box>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <div>
                <label className="block text-[12px] font-semibold text-[#333333] mb-1">
                  First name *
                </label>
                <input
                  type="text"
                  placeholder="Enter first name"
                  value={newReference.first_name}
                  onChange={(e) => onInputChange("first_name", e.target.value)}
                  className={`w-full bg-[#F8F8F8]  rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    referenceErrors.first_name ? "border-red-500 border" : ""
                  }`}
                />
                {referenceErrors.first_name && (
                  <Typography className="text-red-500 text-xs mt-1">
                    {referenceErrors.first_name}
                  </Typography>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-[#333333] mb-1">
                  Last name *
                </label>
                <input
                  type="text"
                  placeholder="Enter last name"
                  value={newReference.last_name}
                  onChange={(e) => onInputChange("last_name", e.target.value)}
                  className={`w-full bg-[#F8F8F8]  rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    referenceErrors.last_name ? "border-red-500 border" : ""
                  }`}
                />
                {referenceErrors.last_name && (
                  <Typography className="text-red-500 text-xs mt-1">
                    {referenceErrors.last_name}
                  </Typography>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-[#333333] mb-1">
                  Email (optional)
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={newReference.email}
                  onChange={(e) => onInputChange("email", e.target.value)}
                  className={`w-full bg-[#F8F8F8]  rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    referenceErrors.email ? "border-red-500 border" : ""
                  }`}
                />
                {referenceErrors.email && (
                  <Typography className="text-red-500 text-xs mt-1">
                    {referenceErrors.email}
                  </Typography>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-[#333333] mb-1">
                  Phone number *
                </label>
                <InputMask
                  mask="(999) 999-9999"
                  maskChar=""
                  value={newReference.phone}
                  onChange={(e) => onInputChange("phone", e.target.value)}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type="tel"
                      placeholder="Enter phone number"
                      className={`w-full bg-[#F8F8F8]   rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        referenceErrors.phone ? "border-red-500 border" : ""
                      }`}
                    />
                  )}
                </InputMask>
                {referenceErrors.phone && (
                  <Typography className="text-red-500 text-xs mt-1">
                    {referenceErrors.phone}
                  </Typography>
                )}
              </div>
            </div>
          </Box>
        )}

        {/* Add Reference Button */}
        <Button
          sx={{
            color: "#2C8D48",
            backgroundColor: "#F2FBF4",
            padding: "12px 16px",
            letterSpacing: "0.025em",
          }}
          onClick={() => setShowAddReference(true)}
          startIcon={<AddIcon />}
        >
          Add reference(s)
        </Button>

        {/* Validation Message */}
        {/* {errors.references && touched.references && (
          <Typography className="text-red-500 text-sm mt-2">
            {errors.references}
          </Typography>
        )} */}

        {/* Minimum requirement message */}
        {references.length < 3 && (
          <Typography variant="h5" className="text-[#FB6E0A]">
            Minimum of 3 references is required!
          </Typography>
        )}
      </div>
    </div>
  );
};

export default Step6;
